import React from 'react';
import { Button, Form, Input, Space, Flex, Title } from '@bb/elements-web';
import styled from 'styled-components';
import { useSingleAccessSetupContext } from '../context/SingleAccessSetupContext';
import { StyledCard, StyledText } from './styledComponents';

export default function SingleAccessDefaultCard() {
  const { submitProfile, userData } = useSingleAccessSetupContext();

  const submit = async ({
    email,
    firstName,
    lastName,
  }: {
    email: string;
    firstName: string;
    lastName: string;
  }) => {
    await submitProfile({
      email,
      firstName,
      lastName,
    });
  };

  return (
    <Flex vertical gap={32} align="center">
      {!!userData && (
        <>
          <StyledCard $bordered>
            <Space size={24} direction="vertical">
              <Space size={8} direction="vertical">
                <Title dataCy="single-access-signup-title" level={4} margin={0}>
                  Sign up for Access
                </Title>
                <StyledText size="large" $secondary>
                  Enter your information to get started and create your account.
                </StyledText>
              </Space>
              <StyledForm
                dataCy="single-access-signup-form"
                layout="vertical"
                onFinish={submit}
              >
                <StyledFormItem
                  label="First name"
                  name="firstName"
                  rules={[
                    { required: true, message: 'Please input your first name' },
                  ]}
                >
                  <Input dataCy="single-access-first-name-input" />
                </StyledFormItem>
                <StyledFormItem
                  label="Last name"
                  name="lastName"
                  rules={[
                    { required: true, message: 'Please input your last name' },
                  ]}
                >
                  <Input dataCy="single-access-last-name-input" />
                </StyledFormItem>
                <StyledFormItem
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Please input your email' },
                  ]}
                  style={{ marginBottom: '16px' }}
                >
                  <Input dataCy="single-access-email-input" />
                </StyledFormItem>
                <StyledFormItem>
                  <StyledButton
                    dataCy="single-access-continue-button"
                    type="primary"
                    htmlType="submit"
                  >
                    Continue
                  </StyledButton>
                </StyledFormItem>
              </StyledForm>
            </Space>
          </StyledCard>
          <StyledContainer>
            <StyledText size="large" $alignCenter>
              By clicking “Continue” you acknowledge that you have read,
              understood, and agree to the{' '}
              <a
                href="https://www.brokerbay.com/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              ,{' '}
              <a
                href="https://buildings.honeywell.com/us/en/brands/our-brands/supra-systems/legal/end-user-license-agreement"
                target="_blank"
                rel="noopener noreferrer"
              >
                EULA
              </a>
              , and{' '}
              <a
                href="https://www.honeywell.com/us/en/privacy-statement"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Notice
              </a>
              .
            </StyledText>
          </StyledContainer>
        </>
      )}
      <StyledFooter>
        <StyledText size="large">
          <a
            href="https://honpat.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Honeywell Patents
          </a>
        </StyledText>
      </StyledFooter>
    </Flex>
  );
}

const StyledContainer = styled.div`
  width: 343px;
  max-width: 90%;
  min-width: 320px;
  text-align: center;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  .ant-input,
  .ant-form-item-label > label {
    font-size: 16px;
  }
`;

const StyledFormItem = styled(Form.Item)`
  width: 100%;
  margin-bottom: 0;
`;

const StyledButton = styled(Button)`
  width: 100%;
  font-size: 16px;
  padding-top: 0;
  padding-bottom: 0;
`;

const StyledFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 12px 16px;
  /* respect device safe area (iOS home indicator / Android gesture bar) */
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
`;
